import React, { useCallback } from 'react';
import { useDropzone, type Accept } from 'react-dropzone';
import { AlertCircle, Download, FileText, Upload } from 'lucide-react';

export type BulkDocumentUploadZoneProps = {
  onDropAccepted: (files: File[]) => void | Promise<void>;
  accept: Accept;
  /** When false, only the first file is passed to the handler (typical for single-sheet process upload). */
  multiple?: boolean;
  disabled?: boolean;
  /** Section heading above the dashed drop area (e.g. “Upload Control Documents”). */
  sectionTitle: string;
  sectionSubtitle: string;
  /** Title inside the dashed card. */
  cardTitle: string;
  cardSubtitle: string;
  formatsFootnote: string;
  chooseFilesLabel?: string;
  onDownloadSample?: () => void | Promise<void>;
  sampleButtonLabel?: string;
  /**
   * Persistent inline error (e.g. spreadsheet header validation). Rendered below the title / sample row in red until the parent clears it.
   */
  inlineAlertMessage?: string | null;
};

/**
 * Dashed dropzone + “Choose files” control, shared by control bulk extraction and process bulk upload.
 */
export function BulkDocumentUploadZone({
  onDropAccepted,
  accept,
  multiple = true,
  disabled = false,
  sectionTitle,
  sectionSubtitle,
  cardTitle,
  cardSubtitle,
  formatsFootnote,
  chooseFilesLabel = 'Choose Files',
  onDownloadSample,
  sampleButtonLabel = 'Template Download',
  inlineAlertMessage = null,
}: BulkDocumentUploadZoneProps): React.ReactElement {
  const handleDrop = useCallback(
    async (acceptedFiles: File[]) => {
      if (acceptedFiles.length === 0) return;
      await onDropAccepted(multiple ? acceptedFiles : acceptedFiles.slice(0, 1));
    },
    [multiple, onDropAccepted]
  );

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop: handleDrop,
    accept,
    multiple,
    noClick: true,
    disabled,
  });

  return (
    <div>
      <div className="mb-4">
        <div className="flex items-center gap-3">
          <h3 className="min-w-0 flex-1 text-base font-semibold leading-tight text-gray-900">{sectionTitle}</h3>
          {onDownloadSample ? (
            <button
              type="button"
              onClick={() => void onDownloadSample()}
              disabled={disabled}
              className="inline-flex min-h-[2.25rem] shrink-0 items-center justify-center gap-2 rounded-full border border-gray-300 bg-white px-3 py-1.5 text-sm font-semibold text-gray-800 shadow-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Download className="h-4 w-4 shrink-0 text-gray-700" aria-hidden />
              {sampleButtonLabel}
            </button>
          ) : null}
        </div>
        <p className="mt-1 text-sm text-gray-500">{sectionSubtitle}</p>
      </div>

      {inlineAlertMessage ? (
        <div
          role="alert"
          className="mb-4 flex gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-800 shadow-sm"
        >
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-600" aria-hidden />
          <span>{inlineAlertMessage}</span>
        </div>
      ) : null}

      <div
        {...getRootProps()}
        className={`cursor-pointer rounded-xl border-2 border-dashed p-12 text-center transition-colors ${
          isDragActive ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
        } ${disabled ? 'pointer-events-none opacity-60' : ''}`}
      >
        <input {...getInputProps()} />
        <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-lg bg-gray-100">
          <FileText className="h-6 w-6 text-gray-600" />
        </div>
        <h4 className="mb-2 text-lg font-medium text-gray-900">{cardTitle}</h4>
        <p className="mx-auto mb-6 max-w-md text-sm text-gray-500">{cardSubtitle}</p>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            open();
          }}
          disabled={disabled}
          className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <Upload className="h-4 w-4" />
          {chooseFilesLabel}
        </button>
        <p className="mt-4 text-xs text-gray-400">{formatsFootnote}</p>
      </div>
    </div>
  );
}

export default BulkDocumentUploadZone;
